import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { SiteContext } from 'application/adapters/context/SiteContext';
import { DictionaryContext } from 'application/adapters/context/Dictionary';
import { isEmbeddedScriptRestrictedPath } from 'helpers/urlParams';
import { fontPath } from 'helpers/fonts';
import { FRAME_HEIGHT_MESSAGE_TYPE, FRAME_HEIGHT_REPORTER_SCRIPT } from 'helpers/isolatedFrame';
import { Container, FRAME_BASE_CSS, FRAME_TOKENS, IsolatedScriptFrame } from 'ui/components';

const MAX_HEIGHT = 50000;
const DEFAULT_LANG = 'en';

// No allow-same-origin: the frame gets an opaque origin, so its scripts cannot reach the page, its cookies or its storage
const SANDBOX = 'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox';

// Browser only. A token that resolves empty is left out, so its rule falls back to the browser default
const readSiteTokens = (): string => {
	const rootStyle = getComputedStyle(document.documentElement);

	return FRAME_TOKENS.map((token) => {
		const value = rootStyle.getPropertyValue(token).trim().replace(/[<>]/g, '');
		return value ? `${token}:${value};` : '';
	}).join('');
};

const getSafeLang = (lang?: string): string => (lang && /^[a-z]{2,3}(-[a-z0-9]{2,8})*$/i.test(lang) ? lang : DEFAULT_LANG);

// The site's own font stylesheet; srcdoc resolves it against the page, and its font files need the /fonts/* CORS header
// in next.config.mjs because the frame's opaque origin makes them cross-origin requests
const buildFrameDocument = (html: string, lang: string, tokenCss: string): string =>
	`<!doctype html><html lang="${lang}"><head><meta charset="utf-8"><base target="_blank"><link rel="stylesheet" href="${fontPath}"><style>:root{${tokenCss}}${FRAME_BASE_CSS}</style><script>${FRAME_HEIGHT_REPORTER_SCRIPT}</script></head><body>${html}</body></html>`;

export const EmbeddedScriptFeature: React.FC<Umbraco.EmbeddedScript> = ({ content }) => {
	const router = useRouter();
	const siteContext = useContext(SiteContext);
	const dictionary = useContext(DictionaryContext);
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [isMounted, setIsMounted] = useState(false);
	const [height, setHeight] = useState(0);
	const { accessibleTitle, script } = content?.properties ?? {};

	// Built after mount: readSiteTokens needs the browser, and the frame is client-only anyway
	const srcDoc = useMemo(
		() => (isMounted ? buildFrameDocument(script ?? '', getSafeLang(siteContext.culture), readSiteTokens()) : ''),
		[isMounted, script, siteContext.culture],
	);

	// Client only: a server-rendered frame could post its height before the listener below exists
	useEffect(() => {
		setIsMounted(true);
	}, []);

	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;

			const { data } = event;
			if (data?.type !== FRAME_HEIGHT_MESSAGE_TYPE || typeof data.height !== 'number' || !Number.isFinite(data.height)) return;

			setHeight(Math.min(Math.max(Math.ceil(data.height), 0), MAX_HEIGHT));
		};

		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, []);

	// Third-party code never runs on checkout, account or login pages
	if (!isMounted || !script || isEmbeddedScriptRestrictedPath(router.asPath)) {
		return null;
	}

	const title = accessibleTitle || dictionary.getValue('EmbeddedScript.DefaultTitle', null, 'Embedded content');

	return (
		<Container width="SlimWeb">
			<IsolatedScriptFrame frameRef={iframeRef} srcDoc={srcDoc} title={title} sandbox={SANDBOX} height={height} />
		</Container>
	);
};
